<template>
  <section class="riw-alias" :aria-labelledby="toggleId">
    <div class="riw-alias__head">
      <import-section-toggle
        :id="toggleId"
        ref="toggle"
        :open="open"
        label="Facility name mapping"
        :controls="bodyId"
        @toggle="$emit('update:open', !open)" />
      <!-- The counts double as the collapsed summary; the error count turns red. -->
      <span class="riw-alias__counts" aria-live="polite">
        <span><b>{{ summary.matched }}</b> matched</span><span class="riw-alias__sep">·</span>
        <span :class="{ 'is-warn': summary.needs }"><b>{{ summary.needs }}</b> needs mapping</span><span class="riw-alias__sep">·</span>
        <span :class="{ 'is-warn': summary.skippedRows }"><b>{{ formatNumber(summary.skippedRows) }}</b> rows would be skipped</span>
        <template v-if="errorCount">
          <span class="riw-alias__sep">·</span><span class="is-err"><b>{{ errorCount }}</b> {{ errorCount === 1 ? 'error' : 'errors' }}</span>
        </template>
      </span>
      <span class="riw-alias__saved" title="Facility names are mapped per source: this mapping is stored in the template you save">
        <v-icon size="14">mdi-content-save-outline</v-icon>Saved with the template
      </span>
    </div>

    <div v-if="loadError" class="riw-callout riw-callout--err riw-callout--compact" role="alert">
      <v-icon class="riw-callout__icon">mdi-alert-circle-outline</v-icon>
      <div class="riw-callout__body">{{ loadError }}</div>
    </div>

    <div :id="bodyId" class="riw-acc-body riw-alias__body" :hidden="!open">
      <div :id="listId" ref="list" class="riw-alias__list" role="group" :aria-label="`Facility values in ${columnLabel || 'the file'}`">
        <div class="riw-alias__row riw-alias__row--head" aria-hidden="true">
          <span>Value in “{{ columnLabel }}”</span>
          <span class="riw-alias__rows">Rows</span>
          <span class="riw-alias__arrow"></span>
          <span>Facility in {{ groupName || 'the Facility Group' }}</span>
          <span class="riw-alias__status">Status</span>
        </div>

        <div v-for="item in fileRows" :key="`v-${item.key}`" class="riw-alias__row">
          <span v-if="item.value" class="riw-alias__src" :title="item.value">{{ item.value }}</span>
          <span v-else class="riw-alias__src is-blank">(blank)</span>
          <span class="riw-alias__rows">{{ formatNumber(item.rows) }}</span>
          <span class="riw-alias__arrow"><v-icon size="15">mdi-arrow-right</v-icon></span>
          <div class="riw-alias__target">
            <div v-if="item.mode === 'missing'" class="riw-alias__target-text is-muted">
              <v-icon size="15">mdi-minus-circle-outline</v-icon><span>No Facility — can’t be mapped</span>
            </div>
            <div v-else-if="item.mode === 'exact'" class="riw-alias__target-text">
              <v-icon size="15">mdi-domain</v-icon><span>{{ item.match.facilityName }}</span>
            </div>
            <v-select
              v-else
              class="riw-input"
              :value="item.selectedId"
              :items="members"
              item-text="name"
              item-value="id"
              placeholder="Not mapped — rows skipped"
              :aria-label="`Facility for ${item.value}`"
              :loading="loading"
              dense outlined hide-details clearable
              prepend-inner-icon="mdi-domain"
              @change="setFileAlias(item, $event)" />
          </div>
          <div class="riw-alias__status">
            <span class="riw-pill" :class="`riw-pill--${item.tone}`"><v-icon>{{ item.icon }}</v-icon>{{ item.statusText }}</span>
          </div>
        </div>

        <div
          v-for="(entry, index) in manualEntries"
          :key="`m-${index}`"
          class="riw-alias__row"
          :class="{ 'is-error': manualErrors[index] }">
          <v-text-field
            class="riw-input"
            :value="entry.sourceValue"
            placeholder="Value as in the file"
            :aria-label="`Facility value ${index + 1}`"
            :aria-invalid="manualErrors[index] ? 'true' : null"
            :aria-describedby="manualErrors[index] ? `${listId}-err-${index}` : null"
            dense outlined hide-details
            @input="updateManual(index, 'sourceValue', $event)" />
          <span class="riw-alias__rows riw-dash">—</span>
          <span class="riw-alias__arrow"><v-icon size="15">mdi-arrow-right</v-icon></span>
          <div class="riw-alias__target">
            <v-select
              class="riw-input"
              :value="entry.facilityId"
              :items="members"
              item-text="name"
              item-value="id"
              placeholder="Choose Facility…"
              :aria-label="`Facility for added value ${index + 1}`"
              :loading="loading"
              dense outlined hide-details
              prepend-inner-icon="mdi-domain"
              @change="updateManual(index, 'facilityId', $event)" />
          </div>
          <div class="riw-alias__status">
            <span class="riw-pill riw-pill--skip">Not in file</span>
            <v-btn icon x-small :aria-label="`Remove added value ${index + 1}`" title="Remove" @click="removeManual(index)">
              <v-icon size="16">mdi-close</v-icon>
            </v-btn>
          </div>
          <div v-if="manualErrors[index]" :id="`${listId}-err-${index}`" class="riw-alias__err" role="alert">
            <v-icon size="14">mdi-alert-circle-outline</v-icon>{{ manualErrors[index] }}
          </div>
        </div>
      </div>

      <div class="riw-alias__foot">
        <v-btn small outlined color="primary" @click="addManual">
          <v-icon left small>mdi-plus</v-icon>Add a value not in this file
        </v-btn>
        <span class="riw-field-cap">Names match exactly, ignoring case and spaces. Rows whose Facility is blank or not mapped are skipped and listed after import.</span>
      </div>
    </div>
  </section>
</template>

<script>
import { copyFacilityAliases, facilityAliasErrors } from '../../modules/registerImportContracts';
import ImportSectionToggle from './importWizard/ImportSectionToggle.vue';
import {
  FACILITY_MATCH,
  aliasEntryErrors,
  facilityMatchSkips,
  normalizeFacilityKey,
  resolveFacilityValue,
} from '../../modules/registerImportWizardModel';

let uid = 0;

const STATUS = {
  [FACILITY_MATCH.MISSING]: { tone: 'skip', icon: 'mdi-debug-step-over', text: 'Rows skipped' },
  [FACILITY_MATCH.EXACT]: { tone: 'ok', icon: 'mdi-check', text: 'Exact name' },
  [FACILITY_MATCH.ALIAS]: { tone: 'alias', icon: 'mdi-swap-horizontal', text: 'Mapped' },
  [FACILITY_MATCH.UNMAPPED]: { tone: 'warn', icon: 'mdi-alert-outline', text: 'Needs mapping' },
  [FACILITY_MATCH.AMBIGUOUS]: { tone: 'warn', icon: 'mdi-alert-outline', text: 'Ambiguous name' },
  [FACILITY_MATCH.STALE]: { tone: 'err', icon: 'mdi-link-variant-off', text: 'Not a member' },
};

/**
 * Facility name mapping (IMP-6) — aliases are stored per source (the Excel template or the API
 * source), as `[{ sourceValue, facilityId }]`. Values found in the file are listed with their row
 * counts; values not in this file can be added by hand. Alias beats exact name; blank, duplicate or
 * non-member entries are invalid (same rule as `facilityAliasErrors`).
 *
 * Emitted entries are ordered [file-bound…, added-by-hand…] so a typed value never jumps between
 * the two groups while the user edits it.
 */
export default {
  name: 'FacilityValueMappingEditor',
  components: { ImportSectionToggle },
  props: {
    value: { type: Array, default: () => [] },
    /** Section open state — owned by FieldMapping (Map Fields accordion); `.sync`-friendly. */
    open: { type: Boolean, default: true },
    /** Current member Facilities of the Facility Group ([{ id, name }]) — loaded by FieldMapping. */
    members: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    loadError: { type: String, default: '' },
    /** Distinct values of the Facility column with row counts (summarizeColumnValues). */
    fileValues: { type: Array, default: () => [] },
    columnLabel: { type: String, default: '' },
    groupName: { type: String, default: '' },
  },
  data() {
    const id = `riw-alias-${++uid}`;
    return { fileBoundCount: 0, lastEmitted: null, listId: id, bodyId: `${id}-body`, toggleId: `${id}-toggle` };
  },
  computed: {
    fileKeys() {
      return new Set(this.fileValues.filter((item) => item.key).map((item) => item.key));
    },
    fileBoundEntries() {
      return this.value.slice(0, this.fileBoundCount);
    },
    manualEntries() {
      return this.value.slice(this.fileBoundCount);
    },
    entryErrors() {
      // While members load, "not a member" is unknown — don't flag rows yet (validity stays false).
      if (this.loading) return this.value.map(() => '');
      return aliasEntryErrors(this.value, this.members);
    },
    manualErrors() {
      return this.entryErrors.slice(this.fileBoundCount);
    },
    errorCount() {
      return this.entryErrors.filter(Boolean).length;
    },
    fileRows() {
      const rank = (item) => {
        if (!item.key) return 1;
        return this.members.some((member) => normalizeFacilityKey(member.name) === item.key) ? 2 : 0;
      };
      return [...this.fileValues]
        .sort((a, b) => rank(a) - rank(b))
        .map((item) => {
          const match = resolveFacilityValue(item.value, this.value, this.members);
          const bound = this.fileBoundEntries.find((entry) => normalizeFacilityKey(entry.sourceValue) === item.key);
          const status = STATUS[match.kind];
          let mode = 'select';
          if (match.kind === FACILITY_MATCH.MISSING) mode = 'missing';
          else if (match.kind === FACILITY_MATCH.EXACT && !bound) mode = 'exact';
          return {
            ...item,
            match,
            mode,
            selectedId: bound ? bound.facilityId : null,
            tone: status.tone,
            icon: status.icon,
            statusText: status.text,
          };
        });
    },
    summary() {
      const rows = this.fileRows;
      const named = rows.filter((item) => item.key);
      return {
        matched: named.filter((item) => [FACILITY_MATCH.EXACT, FACILITY_MATCH.ALIAS].includes(item.match.kind)).length,
        needs: named.filter((item) => facilityMatchSkips(item.match.kind)).length,
        skippedRows: rows.filter((item) => facilityMatchSkips(item.match.kind)).reduce((sum, item) => sum + item.rows, 0),
      };
    },
    valid() {
      return !this.loading && !this.loadError && facilityAliasErrors(this.value, this.members).length === 0;
    },
  },
  watch: {
    valid: { immediate: true, handler(value) { this.$emit('validity', value); } },
    value: {
      immediate: true,
      handler(entries) {
        // Our own emit keeps the partition we chose; anything else (template load, reset) is
        // re-partitioned: the first entry per file value is file-bound, the rest were added by hand.
        if (this.lastEmitted && entries === this.lastEmitted) return;
        this.lastEmitted = null;
        this.fileBoundCount = this.partition(entries).fileBound.length;
        const ordered = this.partition(entries);
        const reordered = [...ordered.fileBound, ...ordered.manual];
        if (reordered.some((entry, index) => entry !== entries[index])) this.emit(reordered, ordered.fileBound.length);
      },
    },
    fileValues() {
      this.fileBoundCount = this.partition(this.value).fileBound.length;
    },
  },
  methods: {
    formatNumber(value) {
      return Number(value || 0).toLocaleString('en-US');
    },
    partition(entries) {
      const fileBound = [];
      const manual = [];
      const claimed = new Set();
      (entries || []).forEach((entry) => {
        const key = normalizeFacilityKey(entry.sourceValue);
        if (key && this.fileKeys.has(key) && !claimed.has(key)) {
          claimed.add(key);
          fileBound.push(entry);
        } else manual.push(entry);
      });
      return { fileBound, manual };
    },
    /** The newest entries — our last emit may not have reached the `value` prop yet (same tick). */
    current() {
      return this.lastEmitted || this.value;
    },
    emit(entries, fileBoundCount) {
      this.fileBoundCount = fileBoundCount;
      this.lastEmitted = entries;
      this.$emit('input', entries);
    },
    setFileAlias(item, facilityId) {
      const entries = this.current();
      const fileBound = copyFacilityAliases(entries.slice(0, this.fileBoundCount))
        .filter((entry) => normalizeFacilityKey(entry.sourceValue) !== item.key);
      if (facilityId) fileBound.push({ sourceValue: item.value, facilityId });
      this.emit([...fileBound, ...copyFacilityAliases(entries.slice(this.fileBoundCount))], fileBound.length);
    },
    /** Focus the section header (FieldMapping's "Show" action). */
    focusToggle() {
      if (this.$refs.toggle) this.$refs.toggle.focus();
    },
    addManual() {
      if (!this.open) this.$emit('update:open', true);
      this.emit([...copyFacilityAliases(this.current()), { sourceValue: '', facilityId: null }], this.fileBoundCount);
      this.$nextTick(() => {
        const list = this.$refs.list;
        if (!list) return;
        list.scrollTop = list.scrollHeight;
        const inputs = list.querySelectorAll('input[type="text"]');
        if (inputs.length) inputs[inputs.length - 1].focus();
      });
    },
    updateManual(index, key, value) {
      const entries = copyFacilityAliases(this.current());
      entries[this.fileBoundCount + index][key] = key === 'facilityId' ? (value || null) : value;
      this.emit(entries, this.fileBoundCount);
    },
    removeManual(index) {
      const target = this.fileBoundCount + index;
      this.emit(copyFacilityAliases(this.current()).filter((entry, position) => position !== target), this.fileBoundCount);
    },
  },
};
</script>
